import React, { useState, useEffect } from 'react';
import { Box, Fab, Zoom, Paper, IconButton, Slide, Typography, useMediaQuery, useTheme, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import Chat from './Chat';

const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLaptop = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  
  // eslint-disable-next-line no-unused-vars
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  // Check URL parameters and localStorage on mount
  useEffect(() => {
    // Check for URL parameters that indicate chat should be opened
    const urlParams = new URLSearchParams(window.location.search);
    const showChat = urlParams.get('showChat');
    const option = urlParams.get('option');
    const chatSessionId = urlParams.get('chatSession');
    
    // Check localStorage for previous chat state
    const chatWasOpen = localStorage.getItem('chatWasOpen') === 'true';
    const savedChatState = localStorage.getItem('chatState');
    const redirectUrl = localStorage.getItem('redirectUrl');
    const hasChatHistory = localStorage.getItem('acx_chat_history') !== null;
    
    console.log('ChatBubble init - should open chat?', {
      showChat,
      option,
      chatSessionId,
      chatWasOpen,
      hasSavedState: !!savedChatState,
      redirectUrl,
      hasChatHistory
    });
    
    // Automatically open the chat if:
    // 1. URL parameters indicate we should
    // 2. We're coming back from a redirect
    // 3. Chat was previously open
    // 4. We have chat history in localStorage
    if (showChat === 'true' || option || chatSessionId || redirectUrl || hasChatHistory || (chatWasOpen && savedChatState)) {
      setIsOpen(true);
    }
  }, []);
  
  // Save chat state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('chatWasOpen', isOpen ? 'true' : 'false');
    
    // Notify Chat component of state change
    if (window.chatComponent) {
      window.chatComponent.setChatOpen(isOpen);
    }
    
    // Use DOM events to communicate with Chat component
    const event = new CustomEvent('chatBubbleStateChange', { 
      detail: { isOpen } 
    });
    window.dispatchEvent(event);
  }, [isOpen]);

  const handleToggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleRefresh = () => {
    // Reset chat to initial state
    if (window.resetAcxChat) {
      console.log('Resetting chat through window function');
      window.resetAcxChat();
    } else {
      // Fallback: use postMessage and localStorage
      localStorage.removeItem('acx_chat_history');
      window.postMessage({ type: 'RESET_CHAT' }, '*');
      console.log('Cleared chat history for refresh (fallback method)');
    }
    
    // Close the chat window temporarily
    setIsOpen(false);
    
    // Wait a moment, then reload the page and reopen the chat window
    setTimeout(() => {
      // Set localStorage to indicate the chat should reopen after refresh
      localStorage.setItem('chatWasOpen', 'true');
      window.location.reload();
    }, 300);
  };

  // Close chat on escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    
    window.addEventListener('keydown', handleEscKey);
    
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  // Listen for messages from external sources
  useEffect(() => {
    const handleExternalMessage = (event) => {
      // Check if the message is for this component
      if (event.data && event.data.type) {
        switch (event.data.type) {
          case 'OPEN_CHAT':
            setIsOpen(true);
            break;
          case 'CLOSE_CHAT':
            setIsOpen(false);
            break;
          case 'TOGGLE_CHAT':
            setIsOpen(prev => !prev);
            break;
          default:
            // Ignore unknown message types
            break;
        }
      }
    };

    // Add event listener for window messages
    window.addEventListener('message', handleExternalMessage);

    // Clean up
    return () => {
      window.removeEventListener('message', handleExternalMessage);
    };
  }, []);

  // Calculate chat window dimensions based on screen size
  const getChatWindowStyles = () => {
    // Base styles for all devices
    const baseStyles = {
      position: 'absolute',
      bottom: isMobile ? 0 : 80,
      right: 0,
      borderRadius: isMobile ? 0 : 2,
      overflow: 'hidden',
      boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
      transition: 'all 0.3s ease',
    };

    // Device-specific adjustments
    if (isMobile) {
      return {
        ...baseStyles,
        position: 'fixed',
        width: '100%',
        height: '100%',
        maxHeight: '100vh',
        borderRadius: 0,
        bottom: 0,
        right: 0,
        zIndex: 2000,
      };
    } else if (isTablet) {
      return {
        ...baseStyles,
        width: 420,
        height: 550,
        maxHeight: '80vh',
      };
    } else if (isLaptop) {
      return {
        ...baseStyles,
        width: 450,
        height: 600,
        maxHeight: '75vh',
      };
    } else { // Desktop
      return {
        ...baseStyles,
        width: 480,
        height: 650,
        maxHeight: '70vh',
      };
    }
  };

  return (
    <>
      {/* Floating Chat Bubble */}
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 20, md: 24, lg: 30 },
          right: { xs: 16, sm: 20, md: 24, lg: 30 },
          zIndex: 1000,
        }}
      >
        <Zoom in={!isOpen}>
          <Fab
            color="primary"
            aria-label="chat"
            onClick={handleToggleChat}
            sx={{
              width: { xs: 56, sm: 60, md: 64 },
              height: { xs: 56, sm: 60, md: 64 },
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              '&:hover': {
                transform: 'scale(1.1)',
                transition: 'transform 0.2s',
                backgroundColor: '#9D1680',
              },
            }}
          >
            <svg viewBox="0 0 24 24" width={isMobile ? "28" : "32"} height={isMobile ? "28" : "32"}>
              <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
              <path d="M8 9h8" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none"></path>
              <path d="M8 13h6" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none"></path>
              <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12z" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" fill="none"></path>
            </svg>
          </Fab>
        </Zoom>

        {/* Chat Window */}
        <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
          <Paper
            elevation={3}
            sx={getChatWindowStyles()}
          >
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Chat Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'primary.main',
                  color: 'white',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '14px' }}>
                      ACX Assistant
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="Refresh Chat">
                    <IconButton
                      size="small"
                      sx={{ color: 'white', mr: 1 }}
                      onClick={handleRefresh}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    onClick={handleToggleChat}
                    size="small"
                    sx={{ color: 'white' }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Chat Content */}
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Chat externalState={{ isOpen }} />
              </Box>
            </Box>
          </Paper>
        </Slide>
      </Box>
    </>
  );
};

export default ChatBubble; 